/**
 * The "Initial import from" rule (DCP-324 AC#9/AC#23) — ONE definition, shared
 * by the UI that shows the field and the sync that obeys it.
 *
 * WHY THIS LIVES IN @dcp/shared AND NOT BESIDE THE SCREEN THAT RENDERS IT
 *
 * The field is only meaningful if the number the user sees is the number the
 * first sync actually uses. While the default lived in `apps/web` that was not
 * true: the screen computed "January 1st of the current year" for a freshly
 * connected bank whose stored `initial_import_from` was still NULL, and
 * `packages/tasks`' first-sync branch read that same NULL as "no lower bound at
 * all" — so a FinBank sandbox connection imported history back to 2026-06-13
 * while the header displayed 01/01/2026. Two copies of a rule cannot be kept
 * honest by review; one copy can.
 *
 * Both consumers therefore derive their bound from {@link resolveInitialImportDate}:
 * the web field renders `value`/`min`, and the sync uses `value` as the `from`
 * of its first pull. The write path (`db-tenant`) enforces the floor that
 * {@link oldestTransactionDate} computes, so a TYPED value cannot sneak past the
 * date picker's greyed-out days.
 */

/** Resolved state of the "Initial import from" field. */
export interface InitialImportDateState {
  /** ISO `YYYY-MM-DD` to show in the DateField (never empty). */
  value: string
  /**
   * Earliest selectable date as ISO `YYYY-MM-DD`, or undefined when the
   * provider has not told us how far back it can go. Fed to `DateField.min`,
   * which greys out earlier days.
   */
  min: string | undefined
}

/**
 * Resolve what the "Initial import from" field shows and how far back it may go.
 *
 * The rule (AC#9), in order of authority:
 *
 *   1. A date the user has ALREADY saved wins outright. It is their decision,
 *      and recomputing a default over it would silently rewrite the field every
 *      time the page is opened in a new year.
 *   2. Otherwise the default is January 1st of the current year — unless that is
 *      EARLIER than the oldest transaction the provider actually has, in which
 *      case the default is that oldest date. Equivalently: the later of the two.
 *      Defaulting to a date with no data behind it would present as a
 *      successful first sync that imported less than the field claimed.
 *   3. The floor (`min`) is always the oldest available date, whatever the
 *      value. In the UI it is an affordance, not enforcement — `DateField` greys
 *      out earlier days in the calendar but a TYPED value still reaches the
 *      change handler, so the save path stays the authority (the data layer
 *      refuses a future date and, since this rule moved here, a date below this
 *      very floor).
 *
 * Both bounds are compared as ISO `YYYY-MM-DD` STRINGS. That is not laziness:
 * ISO calendar dates sort lexicographically exactly as they sort
 * chronologically, so the comparison needs no Date object and therefore cannot
 * shift a day by consulting a timezone — the same reason `DateField` itself
 * does string arithmetic.
 *
 * @param params.stored - Saved `initial_import_from`, or null when never set
 * @param params.oldestTransactionDate - Oldest date the provider has across the
 *   connection's accounts, or null when unknown (never synced)
 * @param params.today - Today as ISO `YYYY-MM-DD`, from `businessDate()`
 * @returns The field's value and its floor
 */
export function resolveInitialImportDate(params: {
  stored: string | null
  oldestTransactionDate: string | null
  today: string
}): InitialImportDateState {
  const { stored, oldestTransactionDate, today } = params
  const januaryFirst = `${today.slice(0, 4)}-01-01`
  const min = oldestTransactionDate ?? undefined

  if (stored) return { value: stored, min }

  // The later of Jan 1 and the oldest available date.
  const value =
    oldestTransactionDate && oldestTransactionDate > januaryFirst
      ? oldestTransactionDate
      : januaryFirst
  return { value, min }
}

/**
 * Reduce a set of accounts to the OLDEST transaction date any of them offers.
 *
 * The minimum is the right reduction: the field's floor should let the user
 * reach as far back as the connection can, and that is whichever account has
 * the deepest history. Accounts with no date (never synced) contribute nothing
 * rather than collapsing the answer to null.
 *
 * Typed structurally (`{ oldest_transaction_date }`) rather than against a row
 * type: the three callers read three different shapes of the same column — the
 * web read model's `ConnectionAccountRow`, the sync task's own narrow
 * `bank_accounts` projection, and the write path's ids-free column select — and
 * naming any one of them here would drag a consumer's types into `@dcp/shared`.
 *
 * @param accounts - Accounts of one connection
 * @returns The oldest ISO date, or null when no account has one
 */
export function oldestTransactionDate(
  accounts: ReadonlyArray<{ oldest_transaction_date: string | null }>,
): string | null {
  let oldest: string | null = null
  for (const account of accounts) {
    const date = account.oldest_transaction_date
    if (!date) continue
    if (!oldest || date < oldest) oldest = date
  }
  return oldest
}
